// LayerContext.tsx
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { MapLayerConfig } from "../types";
import { processLayerUrls } from "../utilities/processLayerUrls";
import { useConfig } from "./ConfigContext";
import { registerContextCleanupHandler, unregisterContextCleanupHandler } from "./UserContext";
import { useAuthSelector } from "../store/auth";

interface FailedLayerInfo {
  id: string;
  error: Error;
  statusCode: number;
}
interface LayerContextType {
  layers: MapLayerConfig[];
  terrainLayers: MapLayerConfig[];
  visibleLayers: Set<string>;
  toggleLayerVisibility: (id: string) => void;
  setLayerVisibility: (id: string, visibility: boolean) => void;
  setFilterLayersVisibility: (visibility: boolean) => void;
  setLayers: React.Dispatch<React.SetStateAction<MapLayerConfig[]>>;
  basicAuthLayerUrls: string[];
  oAuthLayerUrls: string[];
  failedLayers: FailedLayerInfo[];
  addFailedLayer: (id: string, error: Error, statusCode: number) => void;
  refreshLayer: (layerId: string) => void;
  layerRefreshTrigger: Record<string, number>;
  clearAllSessionState: () => void;
}

const LayerContext = createContext<LayerContextType | undefined>(undefined);

export const LayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user: profile } = useAuthSelector();
  const config = useConfig();
  const [layers, setLayers] = useState<MapLayerConfig[]>([]);
  const [visibleLayers, setVisibleLayers] = useState<Set<string>>(new Set());
  const [failedLayers, setFailedLayers] = useState<FailedLayerInfo[]>([]);
  const [layerRefreshTrigger, setLayerRefreshTrigger] = useState<Record<string, number>>({});

  useEffect(() => {
    // Process layers based on the current config
    const initialLayers = processLayerUrls(config.mapLayers).filter(
      (layer) => !failedLayers.some((failedLayer) => failedLayer.id === layer.id)
    );

    // Calculate the new set of initially visible layers based ONLY on the new config
    const newVisibleLayers = new Set<string>();
    initialLayers.forEach((layer) => {
      if (layer.initialVisibility) {
        newVisibleLayers.add(layer.id);
      }
    });

    // Update both layers and visibleLayers state immutably
    setLayers(initialLayers);
    setVisibleLayers(newVisibleLayers);
  }, [profile, config.mapLayers]); // Dependencies remain the same

  const toggleLayerVisibility = (id: string) => {
    setVisibleLayers((prevVisibleLayers) => {
      const newVisibleLayers = new Set(prevVisibleLayers);
      if (newVisibleLayers.has(id)) {
        newVisibleLayers.delete(id);
      } else {
        newVisibleLayers.add(id);
      }
      return newVisibleLayers;
    });
  };

  const setLayerVisibility = (id: string, visibility: boolean) => {
    setVisibleLayers((prevVisibleLayers) => {
      const newVisibleLayers = new Set(prevVisibleLayers);
      if (visibility && !newVisibleLayers.has(id)) {
        newVisibleLayers.add(id);
      } else if (!visibility && newVisibleLayers.has(id)) {
        newVisibleLayers.delete(id);
      }
      return newVisibleLayers;
    });
  };

  const addFailedLayer = (id: string, error: Error, statusCode: number) => {
    setFailedLayers((prevFailedLayers) => [...prevFailedLayers, { id, error, statusCode }]);
    // Also update layers to remove or flag this layer as failed
    setLayers((current) => current.filter((layer) => layer.id !== id));
  };

  //! TODO: This should wait until we make the new request to the server, and only enable visibility once requested.
  const setFilterLayersVisibility = (visibility: boolean) => {
    // Iterate over all layers in the configuration
    for (const layer of layers) {
      // Check if the layer is of type GeoJsonLayerConfig and has a filters property
      if ("filters" in layer && layer.type === "geojson") {
        // Set the visibility of the layer
        setLayerVisibility(layer.id, visibility);
      }
    }
  };

  // Computed states for terrainLayers, basicAuthLayerUrls, and oAuthLayerUrls
  const terrainLayers = layers.filter((layer) => layer.type === "raster-dem");
  const basicAuthLayerUrls = layers.filter((layer) => layer.useBasicAuth).map((layer) => layer.url);
  const oAuthLayerUrls = layers.filter((layer) => layer.useOAuth).map((layer) => layer.url);

  // Include Fulcrum layers as valid visual layers in the map
  const validLayerTypes = ["geojson", "raster", "fulcrum"];

  // Add refresh layer function
  const refreshLayer = (layerId: string) => {
    console.log(`[LayerContext] Refreshing layer: ${layerId}`);
    setLayerRefreshTrigger((prev) => ({
      ...prev,
      [layerId]: (prev[layerId] || 0) + 1
    }));
  };

  // FIX: Must wrap in useCallback to prevent infinite re-render loop in useEffect below
  const clearAllSessionState = useCallback(() => {
    console.log("[LayerContext] Clearing all session state for logout");

    // Reset layer visibility to initial state based on config
    const initialLayers = processLayerUrls(config.mapLayers);
    const newVisibleLayers = new Set<string>();
    initialLayers.forEach((layer) => {
      if (layer.initialVisibility) {
        newVisibleLayers.add(layer.id);
      }
    });

    setVisibleLayers(newVisibleLayers);
    setFailedLayers([]);
    setLayerRefreshTrigger({});

    console.log("[LayerContext] Session state cleared successfully");
  }, [config.mapLayers]);

  // Register cleanup handler for logout
  useEffect(() => {
    registerContextCleanupHandler(clearAllSessionState);
    return () => unregisterContextCleanupHandler(clearAllSessionState);
  }, [clearAllSessionState]);

  return (
    <LayerContext.Provider
      value={{
        layers,
        terrainLayers,
        visibleLayers,
        toggleLayerVisibility,
        setLayerVisibility,
        setFilterLayersVisibility,
        setLayers,
        basicAuthLayerUrls,
        oAuthLayerUrls,
        failedLayers,
        addFailedLayer,
        refreshLayer,
        layerRefreshTrigger,
        clearAllSessionState
      }}
    >
      {children}
    </LayerContext.Provider>
  );
};

export const useLayers = () => {
  const context = useContext(LayerContext);
  if (!context) {
    throw new Error("useLayers must be used within a LayerProvider");
  }
  return context;
};
